import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { FormlyFieldConfig } from '@ngx-formly/core';

interface FieldType {
  key: string;
  fieldArray?: {
    fieldGroup?: FieldType[];
  };
  fieldGroup?: FieldType[];
}

type SpecialFieldType = 'tabsFields' | 'pagesFields' | 'wizardFields';

@Injectable({
  providedIn: 'root',
})
export class FieldProcessorService {
  // Maximum nesting depth to prevent infinite recursion
  private MAX_NESTING_DEPTH = 7;

  /**
   * Process form fields and handle nested structures
   * @param fields - Fields to process
   * @param formBuilderField - Template field config to apply
   */
  processFormFields(fields: FieldType[], formBuilderField: any): void {
    if (!fields || !formBuilderField) {
      console.warn('Missing fields or formBuilderField');
      return;
    }

    // Start at level zero with a maximum of MAX_NESTING_DEPTH
    this.processFieldsRecursively(fields, formBuilderField, 0);
  }

  /**
   * Recursively process fields up to a maximum depth
   * @param fields - Fields to process
   * @param formBuilderField - Template field config to apply
   * @param depth - Current nesting depth
   */
  private processFieldsRecursively(fields: FieldType[], formBuilderField: any, depth: number): void {
    // Stop recursion when maximum depth is reached
    if (depth >= this.MAX_NESTING_DEPTH) {
      // console.warn(`Maximum nesting depth (${this.MAX_NESTING_DEPTH}) reached, stopping recursion`);
      return;
    }

    if (!fields || !fields.length) return;

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];

      // Handle special fields
      if (this.isSpecialField(field)) {
        this.processSpecialField(field, formBuilderField, depth);
      }
      // Handle repeater and group fields
      else if (this.isRepeatOrGroupField(field)) {
        this.setFieldArray(field, formBuilderField);

        // Process the fields inside the fieldArray (if any)
        if (field.fieldArray?.fieldGroup) {
          this.processFieldsRecursively(field.fieldArray.fieldGroup, formBuilderField, depth + 1);
        }
      }

      // Process any fieldGroup recursively
      if (field.fieldGroup) {
        this.processFieldsRecursively(field.fieldGroup, formBuilderField, depth + 1);
      }
    }
  }

  /**
   * Process special fields (tabs, pages, wizard) with depth control
   */
  private processSpecialField(field: FieldType, formBuilderField: any, depth: number): void {
    if (field?.fieldArray?.fieldGroup) {
      for (const groupItem of field.fieldArray.fieldGroup) {
        if (groupItem?.fieldGroup) {
          this.processFieldsRecursively(groupItem.fieldGroup, formBuilderField, depth + 1);
        }

        if (groupItem?.fieldArray?.fieldGroup) {
          this.processFieldsRecursively(groupItem.fieldArray.fieldGroup, formBuilderField, depth + 1);
        }
      }
    }
  }

  /**
   * Check if field is a repeat, group or accordion field.
   *
   * All three hold a list of child fields that the form builder must render with the SAME
   * configuration as a top-level field, so each one gets the builder's field config cloned into
   * its fieldArray. Leaving accordions out here left their children with an empty fieldArray.
   */
  private isRepeatOrGroupField(field: FieldType): boolean {
    return field?.key === 'repeatFields' || field?.key === 'groupFields' || field?.key === 'accordionFields';
  }

  /**
   * Check if field is a special field type
   */
  private isSpecialField(field: FieldType): boolean {
    const specialFields: SpecialFieldType[] = ['tabsFields', 'pagesFields', 'wizardFields'];
    return specialFields.includes(field?.key as SpecialFieldType);
  }

  /**
   * Set the fieldArray property with a deep clone
   */
  private setFieldArray(field: FieldType, formBuilderField: any): void {
    if (!field) return;
    field.fieldArray = cloneDeep(formBuilderField);
  }
}
